import logger from '../lib/logger';
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Heart, ArrowRight, Users, BookOpen, HandHeart, Star, Loader2, AlertCircle, X } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { donationCategories, volunteerOpportunities } from '../data/mock';
import { fetchEvents, submitVolunteerForm } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const KARBALA_IMAGE = "/images/karbala.avif";
const PATTERN_IMAGE = "https://images.unsplash.com/photo-1725710016354-a9da970219c3?w=800&q=60";

// Helper function to parse date string as local date (not UTC)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]), 12, 0, 0);
  }
  return new Date(dateStr + 'T12:00:00');
};

// Smooth fade-in wrapper
const FadeIn = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.7, delay, ease: [0.25, 0.46, 0.45, 0.94] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Hero banner — styled as mourning when shahadat mode is on, otherwise neutral
const ShahadatBanner = ({ message, isMourning }) => {
  if (!message) return null;

  if (isMourning) {
    return (
      <div className="my-10" data-testid="hero-banner">
        <div className="relative overflow-hidden max-w-2xl mx-auto bg-[#0a0000] border border-[#8b3a3a]/30">
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#8b3a3a] to-transparent" />
          <div className="px-10 py-7 text-center">
            <p className="text-white/90 text-xl sm:text-2xl tracking-wide leading-relaxed font-display">
              {message}
            </p>
          </div>
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#8b3a3a] to-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="my-10" data-testid="hero-banner">
      <div className="relative overflow-hidden max-w-2xl mx-auto border border-[#c5a572]/30 bg-white/5 backdrop-blur-sm">
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[#c5a572] to-transparent" />
        <div className="px-10 py-7 text-center">
          <p className="text-white/90 text-xl sm:text-2xl tracking-wide leading-relaxed font-display">
            {message}
          </p>
        </div>
        <div className="h-[2px] bg-gradient-to-r from-transparent via-[#c5a572] to-transparent" />
      </div>
    </div>
  );
};

const HomePage = ({ shahadatMode, shahadatMessage, heroBanner }) => {
  return (
    <div className="min-h-screen">
      <HeroSection shahadatMode={shahadatMode} shahadatMessage={shahadatMessage} heroBanner={heroBanner} />
      <EventsSection />
      <KarbalaSection />
      <DonationSection />
      <VolunteerSection />
      <QuickLinksSection />
    </div>
  );
};

const HeroSection = ({ shahadatMode, shahadatMessage, heroBanner }) => (
  <section className="hero-section" style={{ minHeight: '90vh' }} data-testid="hero-section">
    {/* Rich gradient background — darkened for Muharram */}
    <div className="absolute inset-0 z-0 bg-gradient-to-br from-[#0f1a14] via-[#141f18] to-[#0a0f0c]" />

    {/* Subtle Islamic pattern overlay */}
    <div className="absolute inset-0 z-[1] opacity-[0.07]" style={{
      backgroundImage: `url(${PATTERN_IMAGE})`,
      backgroundSize: '400px',
      backgroundRepeat: 'repeat'
    }} />

    {/* Radial glow accent — muted for Muharram */}
    <div className="absolute inset-0 z-[2]" style={{
      background: 'radial-gradient(ellipse at 50% 40%, rgba(197,165,114,0.06) 0%, transparent 60%)'
    }} />

    {/* Content */}
    <div className="hero-content container mx-auto px-4 text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.2 }}
        className="max-w-4xl mx-auto"
      >
        {/* Decorative Element */}
        <div className="islamic-divider mb-8">
          <div className="islamic-star" />
        </div>

        <h1 className="font-display text-5xl md:text-7xl text-white mb-6 tracking-widest" data-testid="hero-title">
          Welcome to Fatimia
        </h1>
        <p className="text-[#c5a572] text-xl md:text-2xl mb-8 tracking-[0.2em] uppercase">
          Community Services Society of BC
        </p>
        <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed tracking-wide">
          A Farsi Speaking Afghan Shi'a Muslim Centre serving the community with faith, education, and service to humanity.
        </p>

        {/* Hero Banner — set by admin in Settings */}
        {heroBanner && <ShahadatBanner message={heroBanner} isMourning={shahadatMode} />}

        <div className="flex flex-wrap justify-center gap-6 mt-10">
          <Link to="/about" className="btn-primary" data-testid="hero-discover-button">
            Discover Our Community
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link to="/donate" className="btn-secondary border-white/40 text-white hover:bg-white hover:text-[#1a2f23]" data-testid="hero-donate-button">
            Support Our Mission
          </Link>
        </div>

        {/* Decorative Element */}
        <div className="islamic-divider mt-16">
          <div className="islamic-star" />
        </div>
      </motion.div>
    </div>

    {/* Scroll Indicator */}
    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        className="w-6 h-10 border-2 border-[#c5a572]/50 rounded-full flex justify-center"
      >
        <div className="w-1 h-3 bg-[#c5a572] rounded-full mt-2" />
      </motion.div>
    </div>
  </section>
);

const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const data = await fetchEvents();
        setEvents(data);
      } catch (err) {
        logger.error('Failed to fetch events:', err);
        setError('Unable to load events');
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, []);

  // Event Modal Component
  const EventModal = ({ event, onClose }) => {
    React.useEffect(() => {
      const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
      return () => {
        document.removeEventListener('keydown', handleEsc);
        document.body.style.overflow = '';
      };
    }, [onClose]);

    return (
      <div className="fixed inset-0 z-[9999]" onClick={onClose} data-testid="event-modal">
        <div className="fixed inset-0 bg-black/90" />
        <div className="absolute top-[180px] left-0 right-0 bottom-0 flex items-start justify-center px-8 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="relative bg-white w-full max-w-3xl shadow-2xl rounded-lg flex flex-col lg:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left Side - Event Details */}
            <div className="flex-1 p-6 lg:p-8 order-2 lg:order-1">
              <button
                onClick={onClose}
                className="lg:hidden absolute top-3 right-3 w-8 h-8 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex items-center justify-center transition-colors"
                aria-label="Close modal"
                data-testid="event-modal-close"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-3 mb-5">
                <div className="w-14 h-14 bg-[#1a2f23] text-white flex flex-col items-center justify-center rounded">
                  <span className="text-[10px] uppercase tracking-wider text-[#c5a572]">
                    {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'short' })}
                  </span>
                  <span className="text-xl font-bold">
                    {parseLocalDate(event.date).getDate()}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl lg:text-2xl font-display text-[#1a2f23] tracking-wide">{event.title}</h2>
                  {event.event_type && event.event_type !== 'general' && (
                    <span className="text-[10px] bg-[#c5a572]/20 text-[#8a7045] px-2 py-0.5 rounded uppercase tracking-wider mt-1 inline-block">
                      {event.event_type}
                    </span>
                  )}
                </div>
              </div>

              <div className="space-y-3 mb-5">
                {[
                  { icon: Calendar, label: 'Date', value: parseLocalDate(event.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) },
                  { icon: Clock, label: 'Time', value: event.time },
                  { icon: MapPin, label: 'Location', value: event.location }
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className="flex items-center gap-3 text-[#5a6d62]">
                    <div className="w-8 h-8 border border-[#c5a572]/30 flex items-center justify-center rounded">
                      <Icon className="w-4 h-4 text-[#c5a572]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#5a6d62]/70">{label}</p>
                      <p className="text-sm text-[#1a2f23] font-medium">{value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[#c5a572]/20 pt-4">
                <h3 className="text-xs uppercase tracking-wider text-[#c5a572] mb-2">About This Event</h3>
                <div className="text-sm text-[#5a6d62] leading-relaxed rich-text-content" dangerouslySetInnerHTML={{ __html: event.description }} />
              </div>

              <div className="mt-6 flex gap-2">
                <Link to="/contact" className="btn-primary text-sm px-4 py-2 flex-1 justify-center" onClick={onClose} data-testid="event-modal-contact">
                  Contact Us
                  <ArrowRight className="w-3 h-3" />
                </Link>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm border border-[#1a2f23]/20 text-[#1a2f23] rounded hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>

            {/* Right Side - Event Poster */}
            <div className="lg:w-[45%] order-1 lg:order-2 relative bg-gray-100 flex items-center justify-center rounded-t-lg lg:rounded-t-none lg:rounded-r-lg overflow-hidden">
              <button
                onClick={onClose}
                className="hidden lg:flex absolute top-3 right-3 z-10 w-8 h-8 bg-white/90 hover:bg-white text-gray-700 rounded-full items-center justify-center transition-colors shadow-lg"
                aria-label="Close modal"
                data-testid="event-modal-close-desktop"
              >
                <X className="w-4 h-4" />
              </button>
              {event.poster_url ? (
                <img src={event.poster_url} alt={event.title} className="w-full h-48 lg:h-full object-contain" />
              ) : (
                <div className="w-full h-48 lg:h-full bg-gradient-to-br from-[#1a2f23] to-[#2d4a3a] flex items-center justify-center">
                  <div className="text-center text-white p-6">
                    <Calendar className="w-12 h-12 text-[#c5a572] mx-auto mb-3" />
                    <p className="text-[#c5a572] uppercase tracking-widest text-xs">Community Event</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    );
  };

  return (
    <section className="section pattern-overlay" data-testid="events-section">
      <div className="container mx-auto px-4 relative z-10">
        <FadeIn>
          <div className="text-center mb-16">
            <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Stay Connected</p>
            <h2 className="heading-2 mb-6">Upcoming Events</h2>
            <div className="islamic-divider">
              <div className="islamic-star" />
            </div>
          </div>
        </FadeIn>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="w-8 h-8 text-[#c5a572] animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <FadeIn>
            <div className="text-center py-16">
              <Calendar className="w-12 h-12 text-[#c5a572]/40 mx-auto mb-4" />
              <p className="text-[#5a6d62]">No upcoming events at this time. Check back soon!</p>
            </div>
          </FadeIn>
        ) : (
          <div className="grid-3">
            {events.slice(0, 3).map((event, index) => (
              <FadeIn key={event.id} delay={index * 0.12}>
                <div
                  className="card card-gold group overflow-hidden cursor-pointer"
                  onClick={() => setSelectedEvent(event)}
                  data-testid={`event-card-${index}`}
                >
                  {/* Event Poster */}
                  {event.poster_url && (
                    <div className="relative -mx-8 -mt-8 mb-6 h-48 overflow-hidden">
                      <img
                        src={event.poster_url}
                        alt={event.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      <div className="absolute bottom-4 left-4">
                        <span className="text-[#c5a572] text-xs uppercase tracking-[0.25em] bg-black/30 px-2 py-1 backdrop-blur-sm">
                          {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'long' })}
                        </span>
                        <div className="text-4xl text-white mt-1 tracking-wider font-bold drop-shadow-lg">
                          {parseLocalDate(event.date).getDate()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Date display for events without poster */}
                  {!event.poster_url && (
                    <div className="mb-6">
                      <span className="text-[#c5a572] text-xs uppercase tracking-[0.25em]">
                        {parseLocalDate(event.date).toLocaleDateString('en-US', { month: 'long' })}
                      </span>
                      <div className="text-5xl text-[#1a2f23] mt-1 tracking-wider">
                        {parseLocalDate(event.date).getDate()}
                      </div>
                    </div>
                  )}

                  <h3 className="text-xl text-[#1a2f23] mb-4 group-hover:text-[#c5a572] transition-colors tracking-wide font-medium">
                    {event.title}
                  </h3>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-3 text-[#5a6d62]">
                      <Clock className="w-4 h-4 text-[#c5a572]" />
                      <span className="text-sm tracking-wide">{event.time}</span>
                    </div>
                    <div className="flex items-center gap-3 text-[#5a6d62]">
                      <MapPin className="w-4 h-4 text-[#c5a572]" />
                      <span className="text-sm tracking-wide">{event.location}</span>
                    </div>
                  </div>

                  <div className="text-sm text-[#5a6d62] leading-relaxed tracking-wide line-clamp-3 rich-text-content" dangerouslySetInnerHTML={{ __html: event.description }} />

                  <p className="text-xs text-[#c5a572] mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Click for more details →
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        )}

        {selectedEvent && <EventModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />}

        <FadeIn delay={0.2}>
          <div className="text-center mt-12">
            <Link to="/events" className="btn-secondary" data-testid="view-events-button">
              View All Events
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

const KarbalaSection = () => (
  <section className="relative py-32 overflow-hidden" data-testid="karbala-section">
    {/* Background Image */}
    <div
      className="absolute inset-0 z-0"
      style={{
        backgroundImage: `url(${KARBALA_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    />

    {/* Overlay */}
    <div className="absolute inset-0 bg-gradient-to-r from-[#0f1a14]/95 via-[#0f1a14]/80 to-[#0f1a14]/95 z-[1]" />

    {/* Content */}
    <div className="container mx-auto px-4 relative z-10">
      <FadeIn>
        <div className="max-w-3xl mx-auto text-center">
          <div className="islamic-divider mb-8">
            <div className="islamic-star" />
          </div>

          <h2 className="text-4xl md:text-5xl text-white mb-8">
            In the Path of the Ahlul-Bayt
          </h2>

          <p className="text-white/70 text-lg leading-relaxed mb-8">
            As followers of the Shia tradition, we believe in the importance of seeking knowledge,
            cultivating virtue, and serving in the way of the Ahlul-Bayt (as). Our centre is a place
            where you can find resources to help you on this path.
          </p>

          <div className="quote-box bg-white/5 border-[#c5a572] backdrop-blur-sm">
            <p className="text-white/80 text-lg italic leading-relaxed">
              "Indeed, Allah intends only to remove from you the impurity [of sin], O people of the
              [Prophet's] household, and to purify you with [extensive] purification."
            </p>
            <p className="text-[#c5a572] mt-4 font-display text-lg">— Quran 33:33</p>
          </div>

          <div className="islamic-divider mt-12">
            <div className="islamic-star" />
          </div>
        </div>
      </FadeIn>
    </div>
  </section>
);

const iconMap = {
  Heart: Heart,
  Building2: BookOpen,
  Star: Star,
  HandHeart: HandHeart,
  Scale: Heart,
  Coins: Heart
};

const DonationSection = () => (
  <section className="section section-alt" data-testid="donation-section">
    <div className="container mx-auto px-4">
      <FadeIn>
        <div className="text-center mb-16">
          <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Make a Difference</p>
          <h2 className="heading-2 mb-6">Support Our Community</h2>
          <p className="text-[#5a6d62] max-w-2xl mx-auto">
            Your generous contributions help us maintain the mosque, support Islamic education, and serve those in need.
          </p>
          <div className="islamic-divider mt-6">
            <div className="islamic-star" />
          </div>
        </div>
      </FadeIn>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
        {donationCategories.slice(0, 6).map((category, i) => {
          const IconComponent = iconMap[category.icon] || Heart;
          return (
            <FadeIn key={category.id} delay={i * 0.08}>
              <div className="card text-center group cursor-pointer" data-testid={`donation-category-${category.id}`}>
                <div className="w-16 h-16 mx-auto mb-4 border border-[#c5a572]/30 group-hover:border-[#c5a572] group-hover:bg-[#c5a572]/10 flex items-center justify-center transition-all duration-300">
                  <IconComponent className="w-7 h-7 text-[#c5a572]" />
                </div>
                <h3 className="text-lg text-[#1a2f23] mb-2 group-hover:text-[#c5a572] transition-colors">{category.title}</h3>
                <p className="text-xs text-[#5a6d62]">{category.description}</p>
              </div>
            </FadeIn>
          );
        })}
      </div>

      <FadeIn delay={0.3}>
        <div className="text-center">
          <Link to="/donate" className="btn-primary" data-testid="donate-now-button">
            <Heart className="w-5 h-5" />
            Donate Now
          </Link>
        </div>
      </FadeIn>
    </div>
  </section>
);

const VolunteerSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', interest: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await submitVolunteerForm(formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', interest: '' });
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      logger.error('Failed to submit volunteer form:', err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section" data-testid="volunteer-section">
      <div className="container mx-auto px-4">
        <div className="grid-2 items-center">
          {/* Info Side */}
          <FadeIn>
            <div>
              <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Join Our Team</p>
              <h2 className="heading-2 mb-6">Volunteer With Us</h2>
              <p className="text-[#5a6d62] mb-8 leading-relaxed">
                Join our dedicated team of volunteers and make a difference in our community.
                There are many ways to contribute your time and talents.
              </p>

              <div className="space-y-6">
                {volunteerOpportunities.map((opp, i) => (
                  <motion.div
                    key={opp.id}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                    viewport={{ once: true }}
                    className="gold-border-left"
                  >
                    <h4 className="text-lg text-[#1a2f23]">{opp.title}</h4>
                    <p className="text-sm text-[#5a6d62]">{opp.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </FadeIn>

          {/* Form Side */}
          <FadeIn delay={0.15}>
            <div className="card card-gold">
              <h3 className="text-2xl text-[#1a2f23] mb-2">Sign Up to Volunteer</h3>
              <p className="text-sm text-[#5a6d62] mb-6">Fill out this form and we'll be in touch soon.</p>

              {submitted ? (
                <div className="text-center py-12">
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="w-20 h-20 mx-auto mb-6 border-2 border-[#c5a572] flex items-center justify-center">
                      <Heart className="w-10 h-10 text-[#c5a572]" />
                    </div>
                    <h3 className="text-2xl text-[#1a2f23] mb-2">Thank You!</h3>
                    <p className="text-[#5a6d62]">We appreciate your interest in volunteering.</p>
                  </motion.div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="volunteer-form">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {error}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="vol-name" className="text-sm uppercase tracking-wider text-[#5a6d62]">Full Name</Label>
                    <Input
                      id="vol-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      className="mt-2"
                      required
                      data-testid="volunteer-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vol-email" className="text-sm uppercase tracking-wider text-[#5a6d62]">Email</Label>
                    <Input
                      id="vol-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="mt-2"
                      required
                      data-testid="volunteer-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vol-phone" className="text-sm uppercase tracking-wider text-[#5a6d62]">Phone Number</Label>
                    <Input
                      id="vol-phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(604) 555-0123"
                      className="mt-2"
                      data-testid="volunteer-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="vol-interest" className="text-sm uppercase tracking-wider text-[#5a6d62]">Area of Interest</Label>
                    <Select value={formData.interest} onValueChange={(value) => setFormData({ ...formData, interest: value })}>
                      <SelectTrigger className="mt-2" data-testid="volunteer-interest-select">
                        <SelectValue placeholder="Select an area" />
                      </SelectTrigger>
                      <SelectContent>
                        {volunteerOpportunities.map((opp) => (
                          <SelectItem key={opp.id} value={opp.title}>{opp.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full btn-primary mt-6" disabled={submitting} data-testid="volunteer-submit-button">
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </Button>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};

const QuickLinksSection = () => (
  <section className="section-dark py-24 relative overflow-hidden" data-testid="quick-links-section">
    {/* Pattern Overlay */}
    <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `url(${PATTERN_IMAGE})`,
      backgroundSize: '300px',
      backgroundRepeat: 'repeat'
    }} />

    <div className="container mx-auto px-4 relative z-10">
      <FadeIn>
        <div className="text-center mb-16">
          <p className="text-[#c5a572] uppercase tracking-[0.3em] text-sm mb-4">Explore</p>
          <h2 className="text-4xl md:text-5xl text-white mb-6 font-display tracking-widest">Our Programs</h2>
          <div className="islamic-divider">
            <div className="islamic-star" />
          </div>
        </div>
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { to: '/madrassah', icon: BookOpen, title: 'Madrassah', desc: 'Islamic education for children and youth of all ages.' },
          { to: '/youth-program', icon: Users, title: 'Youth Program', desc: 'Engaging activities and development for ages 14-30.' },
          { to: '/membership', icon: HandHeart, title: 'Membership', desc: 'Become an Associated Member and support our community.' }
        ].map((link, i) => (
          <FadeIn key={link.to} delay={i * 0.12}>
            <Link
              to={link.to}
              className="group block p-8 border border-[#c5a572]/20 hover:border-[#c5a572]/50 bg-white/5 hover:bg-white/10 transition-all duration-500"
              data-testid={`quick-link-${link.title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <link.icon className="w-12 h-12 mb-6 text-[#c5a572] group-hover:scale-110 transition-transform duration-300" />
              <h3 className="text-2xl text-white mb-3 group-hover:text-[#c5a572] transition-colors">{link.title}</h3>
              <p className="text-white/60 text-sm mb-6">{link.desc}</p>
              <span className="inline-flex items-center gap-2 text-[#c5a572] text-sm uppercase tracking-wider group-hover:gap-4 transition-all">
                Learn More <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

export default HomePage;
